/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Tearsheet } from '@carbon/ibm-products';
import { Loading } from '@carbon/react';
import { useContext, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import sharedMessages from 'Components/utils/sharedMessages';
import { AllEntitiesContext } from 'Screens/Dashboard/AllEntities/AllEntitiesProvider/AllEntitiesProvider';
import GeneralDetails from 'Screens/Dashboard/AllEntities/Details/GeneralDetails';
import { useUpdateEntityMutation } from 'api/entityEndpoints';
import { useEntityContext } from 'Screens/Dashboard/AllEntities/EntityProvider/EntityContext';
import { EMAIL_PATTERN } from 'utils/constants';

import styles from './EditEntity.module.scss';

const EditEntity = () => {
    const intl = useIntl();
    const context = useContext(AllEntitiesContext);
    const open = context?.currentEntityId !== null;
    const [updateEntity, { isLoading, isError }] = useUpdateEntityMutation();
    const { state } = useEntityContext();

    const isEmailValid = useMemo(
        () => EMAIL_PATTERN.test(state.email) || state.email.length === 0,
        [state.email],
    );
    const isSubmitDisabled = state.name.length === 0
        || (!isEmailValid && state.email.length > 0)
        || (state.isSubgroup && !state.parentId);

    const onClose = () => {
        context?.setCurrentEntityId(null);
    };

    const handleFormSubmit = async () => {
        try {
            await updateEntity({
                entityId: context?.currentEntityId ?? '',
                body: {
                    name: state.name,
                    phoneNumber: state.phoneNumber,
                    email: state.email,
                    website: state.website,
                    isEnabled: true,
                    tags: state.tags.filter((tag) => !tag.isRequired).map((tag) => ({
                        name: tag.name,
                    })),
                    usesPoints: state.usesPoints,
                },
            }).unwrap();
            onClose();
            return Promise.resolve();
        } catch {
            return Promise.reject(new Error('Update entity failed'));
        }
    };

    return (
        <>
            {isLoading && <Loading />}
            <Tearsheet
                open={open}
                title={intl.formatMessage(messages.editGroup)}
                description={intl.formatMessage(messages.description)}
                closeIconDescription={intl.formatMessage(sharedMessages.close)}
                hasCloseIcon
                onClose={onClose}
                selectorPrimaryFocus="#name"
                actions={[
                    {
                        label: intl.formatMessage(sharedMessages.cancel),
                        kind: 'secondary',
                        onClick: onClose,
                    },
                    {
                        label: intl.formatMessage(sharedMessages.submit),
                        kind: 'primary',
                        disabled: isLoading || isSubmitDisabled,
                        loading: isLoading,
                        onClick: handleFormSubmit,
                    },
                ]}
            >
                <div className={styles.form}>
                    {isError && (
                        <div className={styles.error}>
                            {intl.formatMessage(messages.submitError)}
                        </div>
                    )}
                    <GeneralDetails withStep={false} mode={state.isIndependent || !state.parentId ? 'edit' : 'parishGroup'} showUsesPoints />
                    {isLoading && <Loading />}
                </div>
            </Tearsheet>
        </>
    );
};

const messages = defineMessages({
    editGroup: {
        id: 'src.Screens.Dashboard.Entity.editGroup',
        defaultMessage: 'Edytuj grupę',
    },
    description: {
        id: 'src.Screens.Dashboard.Entity.editDescription',
        defaultMessage: 'Zmień podstawowe informacje o grupie i zapisz zmiany.',
    },
    submitError: {
        id: 'src.Screens.Dashboard.Entity.editSubmitError',
        defaultMessage: 'Nie udało się zaktualizować grupy. Sprawdź dane i spróbuj ponownie.',
    },
});

export default EditEntity;
